<template>
  <div>
    <v-dialog v-model="isOpen" @keydown.esc="isOpen=false" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span data-cy="addProduct-title" class="headline">{{ $t('products.modals.addProduct.title') }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="form.valid">
            <v-row>
              <v-col cols="12">
                <v-combobox
                  data-cy="addProduct-name"
                  v-model="form.data.product"
                  :label="$t('products.modals.addProduct.name')"
                  :rules="form.rules.product"
                  :search-input.sync="searchProduct"
                  :loading="form.loading"
                  :items="products"
                  item-text="name"
                  item-value="name"
                  hide-details
                  dense
                  rounded
                  outlined>
                  <template v-slot:append>
                    <v-tooltip
                      max-width="250"
                      top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          v-on="on"
                          icon
                          color="primary"
                          x-small
                          depressed
                        >
                          <v-icon
                            small
                          >
                            mdi-help
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>Introduce el nombre del producto, quizás ya exista alguno en nuestra base de datos y solo tienes que agregar su presentación</span>
                    </v-tooltip>
                  </template>
                </v-combobox>
              </v-col>
              <v-col v-if="!hasProduct" cols="12">
                <v-select
                  data-cy="addProduct-category"
                  v-model="form.data.category"
                  :label="$t('products.modals.addProduct.departament')"
                  :rules="form.rules.category"
                  :items="categories"
                  item-text="name"
                  item-value="id"
                  hide-details
                  dense
                  rounded
                  outlined
                  @change="searchSeccionProducts(form.data.category)"
                />
              </v-col>
              <v-col v-if="!hasProduct" cols="12">
                <v-select
                  data-cy="addProduct-type"
                  v-model="form.data.type"
                  :label="$t('products.modals.addProduct.type')"
                  :rules="form.rules.type"
                  :items="[
                    { text: 'Producto', value: 'product' },
                    { text: 'Servicio', value: 'service' }
                  ]"
                  item-text="text"
                  item-value="value"
                  hide-details
                  dense
                  rounded
                  outlined
                />
              </v-col>
              <v-col v-if="!hasProduct" cols="12">
                <v-select
                  v-model="form.data.sectionProduct"
                  :label="$t('products.modals.addProduct.sectionProduct')"
                  :items="sectionProducts.data"
                  :disabled="form.data.type === 'service'"
                  item-text="name"
                  item-value="id"
                  hide-details
                  dense
                  rounded
                  outlined
                />
              </v-col>
              <v-col v-if="form.data.variants.length" cols="12">
                <v-list-item v-for="(variant, index) in form.data.variants" :key="`variant-${index}`">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ variant.displayQuantity }}
                      {{ getUnitName(variant.displayUnit) }} de
                      {{ variant.contentQuantity }}
                      {{ getUnitName(variant.contentUnit) }}
                      {{ variant.amount | toFixNumber(2) | numberWithComma | prefix('$') }}
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      fab
                      x-small
                      depressed
                      color="error"
                      @click="removeVariant(index)"
                    >
                      <v-icon color="white">mdi-delete</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-col>
              <v-col cols="12">
                <v-btn
                  data-cy="addProduct-presentation"
                  :disabled="!form.data.product"
                  color="primary"
                  block
                  small
                  depressed
                  @click="openAddVariantDialog">
                  <v-icon small>mdi-plus</v-icon>
                  {{ $t('products.modals.addVariant.title') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="mr-4"
            outlined
            rounded
            small
            depressed
            @click="close">
            {{ $t('products.modals.addProduct.cancel') }}
          </v-btn>
          <v-btn
            data-cy="addProduct-success"
            color="primary"
            class="mr-4"
            rounded
            small
            depressed
            @click="submit">
            {{ $t('products.modals.addProduct.success') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AddVariant ref="addVariant" @add="addVariant" />

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import AddVariant from '@/components/modals/AddVariant'
import { debounce } from 'lodash'

export default {
  components: {
    AddVariant
  },
  data () {
    return {
      isOpen: false,
      overlay: false,
      form: {
        valid: false,
        loading: false,
        data: {
          product: null,
          category: '',
          sectionProduct: '',
          type: '',
          variants: []
        },
        rules: {
          product: [v => !!v || this.$t('validations.product')]
        }
      },
      searchProduct: '',
      products: [],
      sectionProducts: {
        search: '',
        data: [],
        total: 0
      }
    }
  },
  watch: {
    searchProduct: debounce(async function (val) {
      if (val && val.length > 3) {
        this.form.loading = true
        try {
          const params = {
            global: true,
            name: `/${val}/i`,
            skip: 0,
            limit: 20
          }
          const response = await this.$api.get(`/v1/products`, params)
          if (response.data.length === 0) this.form.data.product = val
          this.products = response.data
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.form.loading = false
      }
    }, 600)
  },
  computed: {
    units () {
      return this.$store.state.login.units.data
    },
    categories () {
      return this.$store.state.categories
    },
    hasProduct () {
      return !!this.form.data.product && !!this.form.data.product.uuid
    }
  },
  async mounted () {
    await this.$store.dispatch('getCategories')
  },
  methods: {
    open () {
      this.isOpen = true
    },
    close () {
      this.isOpen = false
      this.reset()
    },
    async submit () {
      if (this.$refs.form.validate()) {
        this.overlay = true
        let sectionProduct = ''

        const product = this.form.data.product.uuid ? this.form.data.product.name : this.form.data.product
        const category = this.form.data.product.uuid ? this.form.data.product.category.id : this.form.data.category
        if (!this.form.data.product.uuid && this.form.data.type === 'product') sectionProduct = this.form.data.sectionProduct
        if (this.form.data.product.uuid) sectionProduct = this.form.data.product.sectionProduct.id
        const type = this.form.data.product.uuid ? this.form.data.product.type : this.form.data.type
        const promises = this.form.data.variants.map(variant => {
          const payload = {
            name: product,
            category: category,
            sectionProduct: sectionProduct,
            type: type,
            displayUnit: variant.displayUnit,
            contentQuantity: variant.contentQuantity,
            contentUnit: variant.contentUnit,
            amount: variant.amount,
            brand: variant.brand,
            yield: variant.yield,
            satCode: variant.satCode,
            amountInvoice: variant.amountInvoice,
            profit: variant.profit,
            description: variant.description,
            taxes: variant.taxes
          }
          return this.$api.post('/v1/catalog', payload)
        })
        try {
          await Promise.all(promises)
          this.$emit('update')
          this.close()
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    reset () {
      this.$refs.form.resetValidation()
      this.form.data = {
        product: null,
        category: '',
        type: '',
        variants: []
      }
    },
    openAddVariantDialog () {
      this.$refs.addVariant.open()
    },
    addVariant (variant) {
      this.form.data.variants.push(variant)
    },
    removeVariant (index) {
      this.form.data.variants.splice(index, 1)
    },
    getUnitName (unitId) {
      const unit = this.units.find(unit => unit.id === unitId)
      return unit ? unit.name : '---'
    },
    async searchSeccionProducts (departament) {
      const responseSectionProducts = await this.$api.get('/v1/sectionProducts', { sort: 'name', departament: departament })
      this.sectionProducts.data = responseSectionProducts.data
    }
  }
}
</script>
